import { useState } from 'react';
import { Plus, UserCog, Shield, Building2, Hotel as HotelIcon, QrCode as QrIcon } from 'lucide-react';
import useFetch from '../../hooks/useFetch';
import { post, get } from '../../api/client';
import { useEvents } from '../../context/EventContext';
import { useToast } from '../../context/ToastContext';
import {
  Button,
  Modal,
  Field,
  Input,
  Select,
  Table,
  Badge,
  PersonCell,
  Loader,
  SectionTitle,
  Tabs,
  SearchInput,
  Pagination,
  label,
} from '../../components/UI';
import { fmtDate } from '../../utils/format';

const STAFF_BLANK = { name: '', mobile: '', email: '', designation: '', password: '', eventIds: [] };
const PORTAL_BLANK = { name: '', email: '', password: '', role: 'corporate_admin', corporate: '', hotel: '', mobile: '' };

export default function StaffAccounts() {
  const toast = useToast();
  const { eventId, events } = useEvents();
  const [tab, setTab] = useState('staff');

  return (
    <>
      <SectionTitle
        title="Staff & portal accounts"
        subtitle="Event staff use the mobile app; portal accounts sign in here on the web"
      />
      <Tabs
        active={tab} onChange={setTab}
        tabs={[
          { key: 'staff', label: 'Event staff', icon: UserCog },
          { key: 'portal', label: 'Portal logins', icon: Shield },
        ]}
      />
      {tab === 'staff' ? <StaffTab eventId={eventId} events={events} toast={toast} /> : <PortalTab toast={toast} />}
    </>
  );
}

function StaffTab({ eventId, events, toast }) {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(STAFF_BLANK);
  const [saving, setSaving] = useState(false);
  const [qrFor, setQrFor] = useState(null);
  const [qrImage, setQrImage] = useState(null);

  const { data: rows, meta, loading, reload } = useFetch(
    '/users', { role: 'staff', page, limit: 25, ...(search ? { search } : {}) }, { withMeta: true },
  );

  const create = async () => {
    if (!form.name.trim() || !form.mobile.trim()) return toast.error('Name and mobile number are required');
    if (!form.eventIds.length) return toast.error('Assign at least one event');
    if (form.password && form.password.length < 6) return toast.error('Password must be at least 6 characters');
    if (form.password && !form.email.trim()) return toast.error('An email is required to set a web password');

    setSaving(true);
    try {
      const { password, eventIds, ...rest } = form;
      const res = await post('/users', { ...rest, role: 'staff', events: eventIds });
      // The web password is set on its own endpoint so it is always hashed.
      if (password) await post(`/users/${res.data._id}/password`, { password });
      toast.success(`${form.name} added to the staff team`);
      setOpen(false);
      setForm(STAFF_BLANK);
      reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleEvent = (id) => setForm((f) => ({
    ...f,
    eventIds: f.eventIds.includes(id) ? f.eventIds.filter((x) => x !== id) : [...f.eventIds, id],
  }));

  const showQr = async (row) => {
    setQrFor(row); setQrImage(null);
    try { setQrImage((await get(`/users/${row._id}/qr`)).image); }
    catch (err) { toast.error(err.message); }
  };

  return (
    <>
      <div style={{ display: 'flex', gap: 11, marginBottom: 16, flexWrap: 'wrap' }}>
        <SearchInput value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search staff…" style={{ flex: 1, minWidth: 220 }} />
        <Button icon={Plus} onClick={() => { setForm({ ...STAFF_BLANK, eventIds: eventId ? [eventId] : [] }); setOpen(true); }}>Add staff member</Button>
      </div>

      <Table
        loading={loading}
        rows={rows}
        empty="No event staff yet — add coordinators, marshals and desk staff here"
        columns={[
          { key: 'name', title: 'Staff member', render: (r) => <PersonCell person={r} /> },
          { key: 'mobile', title: 'Mobile', render: (r) => r.mobile || '—' },
          { key: 'designation', title: 'Role on site', render: (r) => r.designation || '—' },
          { key: 'events', title: 'Events', render: (r) => (
            <Badge tone={r.events?.length ? 'gold' : 'neutral'}>{r.events?.length || 0} assigned</Badge>
          )},
          { key: 'lastLoginAt', title: 'Last active', render: (r) => r.lastLoginAt ? fmtDate(r.lastLoginAt) : 'Never' },
          { key: 'qr', title: '', align: 'right', width: 60, render: (r) => (
            <button onClick={(e) => { e.stopPropagation(); showQr(r); }} title="Staff badge"
              style={{ color: 'var(--gold-500)', padding: 6, display: 'flex' }}>
              <QrIcon size={16} />
            </button>
          )},
        ]}
      />
      <Pagination meta={meta} onChange={setPage} />

      <Modal
        open={open} onClose={() => setOpen(false)}
        title="Add a staff member"
        subtitle="Mobile + OTP signs them into the app; email + password signs them into the web portal"
        footer={
          <>
            <Button variant="subtle" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} loading={saving}>Add staff</Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
          <Field label="Full name" required>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 13 }}>
            <Field label="Mobile number" required>
              <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="9876543210" />
            </Field>
            <Field label="Email">
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </Field>
          </div>
          <Field label="Role on site" hint="Shown on their staff badge">
            <Input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })}
              placeholder="Transport Marshal" />
          </Field>
          <Field
            label="Web password"
            hint="Optional. Set one to let them sign in to the web portal; the app always uses OTP."
          >
            <Input type="password" autoComplete="new-password" value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="At least 6 characters" />
          </Field>

          <Field label="Assigned events" required
            hint="They get full control of every event ticked here, and see nothing outside them">
            <div style={{
              display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 190, overflowY: 'auto',
              border: '1px solid var(--border)', borderRadius: 'var(--r)', padding: 6,
            }}>
              {!events.length && (
                <p style={{ fontSize: 12.5, color: 'var(--text-lo)', padding: 8 }}>No events available yet.</p>
              )}
              {events.map((ev) => (
                <label key={ev._id} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '8px 9px',
                  borderRadius: 'var(--r-sm)', cursor: 'pointer', fontSize: 13,
                  background: form.eventIds.includes(ev._id) ? 'rgba(239,212,138,0.08)' : 'transparent',
                }}>
                  <input type="checkbox" checked={form.eventIds.includes(ev._id)}
                    onChange={() => toggleEvent(ev._id)} style={{ accentColor: 'var(--gold-400)' }} />
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {ev.title}
                  </span>
                  <span style={{ fontSize: 11.5, color: 'var(--text-lo)', flexShrink: 0 }}>{fmtDate(ev.startDate)}</span>
                </label>
              ))}
            </div>
          </Field>
        </div>
      </Modal>

      <Modal open={Boolean(qrFor)} onClose={() => setQrFor(null)} title="Staff badge" subtitle={qrFor?.name} width={380}>
        <div style={{ textAlign: 'center' }}>
          {qrImage ? (
            <div style={{ display: 'inline-block', padding: 16, background: '#fff', borderRadius: 'var(--r-lg)' }}>
              <img src={qrImage} alt="Staff QR" style={{ width: 200, height: 200, display: 'block' }} />
            </div>
          ) : <Loader label="Generating badge" />}
          <p style={{ fontSize: 15, fontWeight: 600, marginTop: 16 }}>{qrFor?.name}</p>
          <p style={{ fontSize: 12.5, color: 'var(--text-mid)', marginTop: 3 }}>{qrFor?.designation}</p>
        </div>
      </Modal>
    </>
  );
}

function PortalTab({ toast }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(PORTAL_BLANK);
  const [saving, setSaving] = useState(false);

  const { data: corporates } = useFetch('/corporates', { limit: 100 });
  const { data: hotels } = useFetch('/hotels', { limit: 100 });

  const admins = useFetch('/users', { role: 'super_admin', limit: 50 }, { withMeta: true });
  const corpAdmins = useFetch('/users', { role: 'corporate_admin', limit: 50 }, { withMeta: true });
  const hotelUsers = useFetch('/users', { role: 'hotel', limit: 50 }, { withMeta: true });

  const rows = [...(admins.data || []), ...(corpAdmins.data || []), ...(hotelUsers.data || [])];
  const loading = admins.loading || corpAdmins.loading || hotelUsers.loading;

  const reloadAll = () => { admins.reload(); corpAdmins.reload(); hotelUsers.reload(); };

  const create = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.password) {
      return toast.error('Name, email and password are required');
    }
    if (form.password.length < 6) return toast.error('Password must be at least 6 characters');
    if (form.role === 'corporate_admin' && !form.corporate) return toast.error('Select the company this admin manages');
    if (form.role === 'hotel' && !form.hotel) return toast.error('Select the property this account manages');

    setSaving(true);
    try {
      await post('/users/portal', {
        name: form.name.trim(), email: form.email.trim(), password: form.password,
        role: form.role, mobile: form.mobile || undefined,
        corporate: form.role === 'corporate_admin' ? form.corporate : undefined,
        hotel: form.role === 'hotel' ? form.hotel : undefined,
      });
      toast.success('Portal account created');
      setOpen(false);
      setForm(PORTAL_BLANK);
      reloadAll();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const ROLE_TONE = { super_admin: 'gold', corporate_admin: 'info', hotel: 'success' };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Button icon={Plus} onClick={() => { setForm(PORTAL_BLANK); setOpen(true); }}>Create portal login</Button>
      </div>

      <Table
        loading={loading}
        rows={rows}
        keyField="_id"
        empty="No portal accounts yet"
        columns={[
          { key: 'name', title: 'Account', render: (r) => <PersonCell person={r} /> },
          { key: 'email', title: 'Email', render: (r) => r.email || '—' },
          { key: 'role', title: 'Role', render: (r) => <Badge tone={ROLE_TONE[r.role] || 'neutral'}>{label(r.role)}</Badge> },
          { key: 'org', title: 'Scope', render: (r) => (
            r.corporate?.name
              ? <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Building2 size={12} color="var(--text-lo)" />{r.corporate.name}</span>
              : r.hotel?.name
                ? <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><HotelIcon size={12} color="var(--text-lo)" />{r.hotel.name}</span>
                : <span style={{ color: 'var(--text-lo)' }}>Platform-wide</span>
          )},
          { key: 'lastLoginAt', title: 'Last login', render: (r) => r.lastLoginAt ? fmtDate(r.lastLoginAt) : 'Never' },
          { key: 'isActive', title: 'Status', render: (r) => <Badge tone={r.isActive ? 'success' : 'danger'} dot>{r.isActive ? 'Active' : 'Disabled'}</Badge> },
        ]}
      />

      <Modal
        open={open} onClose={() => setOpen(false)}
        title="Create a portal login"
        subtitle="Web access for a corporate admin, hotel partner or another super admin"
        footer={
          <>
            <Button variant="subtle" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} loading={saving}>Create account</Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
          <Field label="Role" required>
            <Select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              <option value="corporate_admin">Corporate Admin</option>
              <option value="hotel">Hotel Partner</option>
              <option value="super_admin">Super Admin</option>
            </Select>
          </Field>

          {form.role === 'corporate_admin' && (
            <Field label="Company" required>
              <Select value={form.corporate} onChange={(e) => setForm({ ...form, corporate: e.target.value })}>
                <option value="">Select a company…</option>
                {(corporates || []).map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
              </Select>
            </Field>
          )}
          {form.role === 'hotel' && (
            <Field label="Property" required>
              <Select value={form.hotel} onChange={(e) => setForm({ ...form, hotel: e.target.value })}>
                <option value="">Select a property…</option>
                {(hotels || []).map((h) => <option key={h._id} value={h._id}>{h.name}</option>)}
              </Select>
            </Field>
          )}

          <Field label="Full name" required>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 13 }}>
            <Field label="Email" required>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </Field>
            <Field label="Mobile">
              <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} />
            </Field>
          </div>
          <Field label="Password" required hint="At least 6 characters — share it securely">
            <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </Field>
        </div>
      </Modal>
    </>
  );
}
